import { CalculatorInputs, CalculationResults, WeeklyPlanItem, FormulaResult, TimeProjectionResult } from "@/types/calculator";

/**
 * ACSM Walking Formula for energy expenditure
 * VO2 (ml/kg/min) = 0.1 * speed (m/min) + 1.8 * speed * grade + 3.5
 * Calories = VO2 * weight (kg) * duration (min) / 1000 * 5
 * Citation: ACSM's Guidelines for Exercise Testing and Prescription
 */
const calculateACSMWalkingCalories = (
  weightKg: number,
  speedKmh: number,
  durationMin: number,
  terrain: string
): number => {
  const speedMmin = (speedKmh * 1000) / 60;
  const gradeMap = { flat: 0, moderate: 0.05, hilly: 0.10 };
  const grade = gradeMap[terrain as keyof typeof gradeMap] || 0;
  const vo2 = 0.1 * speedMmin + 1.8 * speedMmin * grade + 3.5;
  const calories = (vo2 * weightKg * durationMin) / 1000 * 5;
  return calories;
};

/**
 * MET-based calculation using Compendium of Physical Activities
 * Calories = MET * weight (kg) * duration (hours)
 * Citation: Ainsworth BE et al. 2011 Compendium of Physical Activities
 */
const calculateMETCalories = (
  weightKg: number,
  speedKmh: number,
  durationMin: number,
  terrain: string
): number => {
  // MET values based on speed and terrain
  let met = 3.0; // Base walking MET
  
  if (speedKmh < 3.2) met = 2.5;
  else if (speedKmh < 4.0) met = 3.0;
  else if (speedKmh < 4.8) met = 3.5;
  else if (speedKmh < 5.6) met = 4.3;
  else if (speedKmh < 6.4) met = 5.0;
  else met = 6.0;

  // Terrain adjustment
  if (terrain === "moderate") met *= 1.2;
  if (terrain === "hilly") met *= 1.5;

  const durationHours = durationMin / 60;
  return met * weightKg * durationHours;
};

/**
 * Calculate BMR using Mifflin-St Jeor equation
 * Citation: Mifflin et al. 1990, American Journal of Clinical Nutrition
 */
const calculateMifflinBMR = (
  weightKg: number,
  heightCm: number,
  age: number,
  sex: string
): number => {
  const baseBMR = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return sex === "male" ? baseBMR + 5 : baseBMR - 161;
};

/**
 * Calculate BMR using Katch-McArdle equation (requires body fat %)
 * Citation: Katch & McArdle 1996
 */
const calculateKatchMcArdleBMR = (
  weightKg: number,
  bodyFatPercent: number
): number => {
  const leanMassKg = weightKg * (1 - bodyFatPercent / 100);
  return 370 + (21.6 * leanMassKg);
};

/**
 * Calculate TDEE based on activity level
 */
const calculateTDEE = (bmr: number, activityLevel: string): number => {
  const multipliers = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    "very active": 1.725,
  };
  return bmr * (multipliers[activityLevel as keyof typeof multipliers] || 1.2);
};

/**
 * Main calculation function with multiple formula support
 */
export const calculateResults = (inputs: CalculatorInputs): CalculationResults => {
  // Convert imperial to metric if needed
  let weightKg = inputs.weight;
  let heightCm = inputs.height;
  let speedKmh = inputs.speed;

  if (inputs.unitSystem === "imperial") {
    weightKg = inputs.weight / 2.20462;
    heightCm = inputs.height * 2.54;
    speedKmh = inputs.speed * 1.60934;
  }

  // Calculate using all selected formulas
  const formulaResults: FormulaResult[] = [];
  
  if (!inputs.selectedFormulas || inputs.selectedFormulas.includes("ACSM")) {
    const acsm = calculateACSMWalkingCalories(weightKg, speedKmh, inputs.duration, inputs.terrain);
    const intensityMultipliers = { easy: 0.9, moderate: 1.0, brisk: 1.15 };
    const adjusted = acsm * (intensityMultipliers[inputs.intensity] || 1.0);
    formulaResults.push({
      formula: "ACSM",
      name: "ACSM Walking Equation",
      description: "Gold standard for walking energy expenditure, accounts for speed and grade",
      caloriesPerSession: adjusted,
      citation: "ACSM's Guidelines for Exercise Testing and Prescription (2021)",
    });
  }

  if (!inputs.selectedFormulas || inputs.selectedFormulas.includes("MET")) {
    const met = calculateMETCalories(weightKg, speedKmh, inputs.duration, inputs.terrain);
    formulaResults.push({
      formula: "MET",
      name: "MET Compendium Method",
      description: "Based on metabolic equivalent values from research database",
      caloriesPerSession: met,
      citation: "Ainsworth BE et al. 2011 Compendium of Physical Activities",
    });
  }

  if (!inputs.selectedFormulas || inputs.selectedFormulas.includes("Mifflin")) {
    const bmr = calculateMifflinBMR(weightKg, heightCm, inputs.age, inputs.sex);
    const tdee = calculateTDEE(bmr, inputs.activityLevel);
    const exerciseCalories = (tdee * 0.3 * inputs.duration) / (24 * 60);
    formulaResults.push({
      formula: "Mifflin",
      name: "Mifflin-St Jeor + Activity",
      description: "BMR-based calculation with activity multiplier",
      caloriesPerSession: exerciseCalories,
      citation: "Mifflin et al. 1990, American Journal of Clinical Nutrition",
    });
  }

  if (inputs.bodyFat && (!inputs.selectedFormulas || inputs.selectedFormulas.includes("Katch"))) {
    const bmr = calculateKatchMcArdleBMR(weightKg, inputs.bodyFat);
    const tdee = calculateTDEE(bmr, inputs.activityLevel);
    const exerciseCalories = (tdee * 0.3 * inputs.duration) / (24 * 60);
    formulaResults.push({
      formula: "Katch",
      name: "Katch-McArdle",
      description: "Uses lean body mass for more accurate individual results",
      caloriesPerSession: exerciseCalories,
      citation: "Katch & McArdle 1996, Exercise Physiology",
    });
  }

  // Use average or primary formula
  const primaryCalories = formulaResults[0]?.caloriesPerSession || 0;
  const avgCalories = formulaResults.reduce((sum, r) => sum + r.caloriesPerSession, 0) / formulaResults.length;

  // Weekly calculations
  const weeklyCalories = avgCalories * inputs.frequency;

  // BMR and TDEE
  const bmr = calculateMifflinBMR(weightKg, heightCm, inputs.age, inputs.sex);
  const adjustedTDEE = calculateTDEE(bmr, inputs.activityLevel);

  // Weight change estimation (7700 kcal ≈ 1 kg fat)
  const weeklyDeficit = weeklyCalories;
  const estimatedWeightChange = weeklyDeficit / 7700;

  // Confidence interval calculation
  const hasBodyFat = !!inputs.bodyFat;
  const hasMultipleFormulas = formulaResults.length > 1;
  const confidencePercent = hasBodyFat && hasMultipleFormulas ? 0.10 : hasBodyFat ? 0.15 : 0.20;
  
  let confidenceReason = "Standard estimation variance";
  if (!hasBodyFat) confidenceReason = "Body composition unknown - wider range";
  if (inputs.speed > 6.5 || inputs.speed < 2.5) confidenceReason = "Unusual speed - increased uncertainty";
  if (inputs.terrain === "hilly") confidenceReason += "; variable terrain adds uncertainty";

  const confidenceInterval = {
    lower: avgCalories * (1 - confidencePercent),
    upper: avgCalories * (1 + confidencePercent),
    reason: confidenceReason,
  };

  // Pace calculation
  const paceMinPerKm = 60 / speedKmh;

  // Steps target (roughly 1250-1500 steps per km, using 1300 average)
  const distanceKm = (speedKmh * inputs.duration) / 60;
  const stepsTarget = Math.round(distanceKm * 1300);

  // Generate 12-week plan
  const weeklyPlan: WeeklyPlanItem[] = [];
  let cumulative = 0;
  for (let week = 1; week <= 12; week++) {
    const weekChange = estimatedWeightChange;
    cumulative += weekChange;
    weeklyPlan.push({
      week,
      sessions: inputs.frequency,
      totalMinutes: inputs.duration * inputs.frequency,
      estimatedCalories: weeklyCalories,
      projectedWeightChange: weekChange,
      cumulativeWeightChange: cumulative,
    });
  }

  // Time projections
  const timeProjections: TimeProjectionResult[] = [
    {
      period: "1week",
      label: "1 Week",
      weeks: 1,
      totalCalories: weeklyCalories,
      totalWeightChange: estimatedWeightChange,
      sessions: inputs.frequency,
    },
    {
      period: "1month",
      label: "1 Month",
      weeks: 4,
      totalCalories: weeklyCalories * 4,
      totalWeightChange: estimatedWeightChange * 4,
      sessions: inputs.frequency * 4,
    },
    {
      period: "3months",
      label: "3 Months",
      weeks: 12,
      totalCalories: weeklyCalories * 12,
      totalWeightChange: estimatedWeightChange * 12,
      sessions: inputs.frequency * 12,
    },
    {
      period: "6months",
      label: "6 Months",
      weeks: 24,
      totalCalories: weeklyCalories * 24,
      totalWeightChange: estimatedWeightChange * 24,
      sessions: inputs.frequency * 24,
    },
  ];

  return {
    caloriesPerSession: avgCalories,
    weeklyCalories,
    estimatedWeightChange,
    adjustedTDEE,
    paceMinPerKm,
    stepsTarget,
    confidenceInterval,
    weeklyPlan,
    formulaResults,
    timeProjections,
  };
};
